if (!customElements.get('escala-garment-rail')) {
  customElements.define('escala-garment-rail', class extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.egr__item'));
      this.viewport = this.querySelector('.egr__viewport');
      this.scene = this.querySelector('.egr__scene');
      this.active = -1;
      this.events = new AbortController();
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = window.matchMedia('(max-width: 749px)');
      const listen = (target, name, callback) => target.addEventListener(name, callback, { signal: this.events.signal });
      this.items.forEach((item, index) => {
        const button = item.querySelector('.egr__select');
        item.querySelectorAll('.egr__image, .egr__resting-image').forEach(image => listen(image, 'load', () => this.alignPhoto(item)));
        listen(button, 'pointerenter', event => {
          if (!this.mobile.matches && event.pointerType === 'mouse' && this.dataset.hover === 'true' && !this.dragging) this.select(index);
        });
        listen(button, 'focus', () => {
          if (button.matches(':focus-visible')) this.select(index, true);
        });
        listen(button, 'click', event => {
          if (this.suppressClick) { event.preventDefault(); return; }
          const wasActive = this.active === index;
          this.select(index, true);
          if (wasActive && this.dataset.designMode !== 'true' && item.dataset.url) {
            const mouse = event.pointerType === 'mouse';
            if (mouse || event.detail === 0 || this.lastClicked === index) window.location.assign(item.dataset.url);
          }
          this.lastClicked = index;
        });
        const video = item.querySelector('video');
        if (video) {
          listen(video, 'loadedmetadata', () => this.alignPhoto(item));
          listen(video, 'playing', () => {
            if (this.active === index && this.canAnimate && !document.hidden && this.inView !== false) item.classList.add('is-playing');
            else video.pause();
          });
          listen(video, 'error', () => { item.classList.remove('is-playing'); item.dataset.videoFailed = 'true'; this.updateFlip(); });
        }
      });
      listen(this, 'keydown', event => {
        if (event.key === 'Escape') { this.select(-1); return; }
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        const index = this.nextIndex(delta);
        if (index < 0) return;
        this.items[index].querySelector('.egr__select').focus({ preventScroll: true });
        this.select(index, true);
      });
      this.querySelectorAll('[data-direction]').forEach(button => listen(button, 'click', () => this.select(this.nextIndex(Number(button.dataset.direction)), true)));
      listen(this.querySelector('.egr__reset'), 'click', () => this.select(this.mobile.matches && this.items.length ? 0 : -1, true));
      listen(this.viewport, 'scroll', () => this.queueCenteredSelection());
      listen(this.viewport, 'pointerdown', event => {
        if (event.pointerType !== 'mouse') {
          this.touchStart = { x: event.clientX, y: event.clientY };
          return;
        }
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.dragStart = { x: event.clientX, y: event.clientY, left: this.viewport.scrollLeft };
      });
      listen(document, 'pointermove', event => {
        if (this.touchStart && Math.abs(event.clientX - this.touchStart.x) > 6) this.touchSwiping = true;
        if (!this.dragStart) return;
        const dx = event.clientX - this.dragStart.x;
        if (!this.dragging && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(event.clientY - this.dragStart.y)) {
          this.dragging = true;
          this.classList.add('is-dragging');
        }
        if (this.dragging) { event.preventDefault(); this.viewport.scrollLeft = this.dragStart.left - dx; }
      });
      const finishDrag = () => {
        if (this.dragging || this.touchSwiping) {
          this.suppressClick = true;
          clearTimeout(this.clickTimer);
          this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 250);
        }
        this.dragStart = null;
        this.touchStart = null;
        this.touchSwiping = false;
        this.dragging = false;
        this.classList.remove('is-dragging');
      };
      listen(document, 'pointerup', finishDrag);
      listen(document, 'pointercancel', finishDrag);
      listen(this.viewport, 'dragstart', event => event.preventDefault());
      listen(this.querySelector('.egr__flip'), 'click', () => {
        if (this.active < 0) return;
        this.items[this.active].classList.toggle('is-back');
        this.updateFlip();
      });
      listen(this, 'pointerleave', event => {
        if (!this.mobile.matches && event.pointerType === 'mouse' && this.dataset.hover === 'true' && !this.contains(document.activeElement)) this.select(-1);
      });
      listen(document, 'shopify:block:select', event => {
        const index = this.items.findIndex(item => item.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.select(index, true);
      });
      listen(document, 'shopify:block:deselect', event => {
        if (this.items.some(item => item.dataset.blockId === event.detail.blockId)) this.select(-1);
      });
      listen(document, 'visibilitychange', () => this.syncVideo());
      listen(this.reduceMotion, 'change', () => { this.layout(); this.syncVideo(); });
      listen(this.mobile, 'change', () => { this.layout(); this.queueCenteredSelection(); });
      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.viewport);
      this.intersectionObserver = new IntersectionObserver(entries => { this.inView = entries[0].isIntersecting; this.syncVideo(); });
      this.intersectionObserver.observe(this);
      this.setAttribute('data-ready', '');
      this.layout();
      if (this.mobile.matches && this.items.length) this.selectCentered();
      if (!this.items.length) this.querySelectorAll('[data-direction], .egr__reset').forEach(button => { button.disabled = true; });
    }
    get canAnimate() { return this.dataset.motion === 'true' && !this.reduceMotion.matches; }
    queueCenteredSelection() {
      if (!this.mobile.matches || this.centerFrame) return;
      this.centerFrame = requestAnimationFrame(() => {
        this.centerFrame = null;
        this.selectCentered();
      });
    }
    selectCentered() {
      if (!this.mobile.matches || !this.items.length || !this.centers) return;
      const center = this.viewport.scrollLeft + this.viewWidth / 2;
      let closest = 0;
      this.centers.forEach((position, index) => {
        if (Math.abs(position - center) < Math.abs(this.centers[closest] - center)) closest = index;
      });
      if (closest !== this.active) this.select(closest);
    }
    nextIndex(delta) {
      if (!this.items.length) return -1;
      if (this.active < 0) return delta > 0 ? 0 : this.items.length - 1;
      return (this.active + delta + this.items.length) % this.items.length;
    }
    select(index, scroll = false) {
      if (index < -1 || index >= this.items.length) return;
      const changed = index !== this.active;
      this.active = index;
      this.items.forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === index);
        item.querySelector('.egr__select').setAttribute('aria-pressed', String(itemIndex === index));
        if (changed) item.classList.remove('is-back');
      });
      this.querySelector('.egr__idle-copy').hidden = index >= 0;
      this.querySelector('.egr__selection').hidden = index < 0;
      this.querySelector('[data-current]').textContent = index < 0 ? '—' : String(index + 1).padStart(2, '0');
      if (index >= 0) {
        const item = this.items[index];
        this.querySelector('[data-product-title]').textContent = item.dataset.title;
        const price = this.querySelector('[data-product-price]');
        if (price) price.textContent = item.dataset.price;
        const link = this.querySelector('[data-product-link]');
        link.hidden = !item.dataset.url;
        if (item.dataset.url) link.setAttribute('href', item.dataset.url);
        else link.removeAttribute('href');
        if (changed) this.querySelector('.egr__announcement').textContent = `${item.dataset.title}, ${item.dataset.price}`;
      } else this.lastClicked = null;
      this.updateFlip();
      this.layout(scroll);
      this.syncVideo();
    }
    updateFlip() {
      const button = this.querySelector('.egr__flip');
      const item = this.items[this.active];
      const video = item?.querySelector('video');
      button.hidden = !item || item.dataset.hasBack !== 'true' || (video && item.dataset.videoFailed !== 'true' && this.canAnimate);
      const back = item?.classList.contains('is-back') || false;
      button.setAttribute('aria-pressed', String(back));
      button.textContent = back ? button.dataset.frontLabel : button.dataset.backLabel;
    }
    layout(scroll = false) {
      const mobile = this.mobile.matches;
      const viewWidth = Math.max(1, this.viewport.clientWidth - (mobile ? 40 : 96));
      const width = Math.min(Number(mobile ? this.dataset.mobileWidth : this.dataset.garmentWidth), mobile ? viewWidth * .82 : viewWidth * .5);
      const angle = Number(this.dataset.sideAngle);
      const projected = width * Math.cos(angle * Math.PI / 180);
      const desiredSpacing = Math.max(Number(this.dataset.idleSpacing), projected + 12);
      const count = this.items.length;
      const spacing = mobile ? desiredSpacing : Math.max(projected + 10, Math.min(desiredSpacing, (viewWidth - (this.active >= 0 ? width : 0) - 30) / Math.max(1, count)));
      const mobileSpacing = Math.max(spacing, (width + 26 + spacing) / 2);
      const slotWidths = this.items.map((_, index) => mobile ? mobileSpacing : index === this.active ? width + 26 : spacing);
      const totalWidth = slotWidths.reduce((a, b) => a + b, 0) + 24;
      const sceneWidth = mobile ? viewWidth + Math.max(0, count - 1) * mobileSpacing : Math.max(viewWidth, totalWidth);
      this.scene.style.width = `${sceneWidth}px`;
      this.style.setProperty('--egr-width', `${width}px`);
      this.viewWidth = viewWidth;
      this.centers = [];
      let cursor = mobile ? (viewWidth - mobileSpacing) / 2 : (sceneWidth - totalWidth) / 2 + 12;
      this.items.forEach((item, index) => {
        const slotWidth = slotWidths[index];
        const itemWidth = width;
        const center = cursor + slotWidth / 2;
        this.centers.push(center);
        item.style.setProperty('--egr-item-width', `${itemWidth}px`);
        item.style.setProperty('--egr-x', `${center - itemWidth / 2}px`);
        item.style.setProperty('--egr-hit-width', `${slotWidth}px`);
        this.alignPhoto(item, itemWidth);
        cursor += slotWidth;
        if (scroll && index === this.active) this.viewport.scrollTo({ left: Math.max(0, center - viewWidth / 2), behavior: this.canAnimate ? 'smooth' : 'auto' });
      });
      if (scroll && this.active < 0) this.viewport.scrollTo({ left: 0, behavior: this.canAnimate ? 'smooth' : 'auto' });
      if (!scroll) this.queueCenteredSelection();
    }
    alignPhoto(item, measuredWidth) {
      const width = measuredWidth || parseFloat(item.style.getPropertyValue('--egr-item-width'));
      if (!Number.isFinite(width)) return;
      const hookWidth = parseFloat(item.style.getPropertyValue('--egr-hanger-width')) / 100;
      const scale = parseFloat(item.style.getPropertyValue('--egr-scale')) || 1;
      item.style.setProperty('--egr-hook-top', `${5 - 8 * scale}px`);
      const collarY = width * hookWidth * 31 / 240 - 3;
      const neckline = Number(item.dataset.neckline || 19) / 100;
      item.querySelectorAll('.egr__face').forEach(face => {
        const image = face.querySelector('img');
        if (!image) return;
        const ratio = (image.naturalHeight || Number(image.getAttribute('height'))) / (image.naturalWidth || Number(image.getAttribute('width')));
        if (!Number.isFinite(ratio)) return;
        face.style.setProperty('--egr-photo-offset', `${collarY - width * ratio * neckline}px`);
      });
      item.querySelectorAll('.egr__resting-image, .egr__video').forEach(media => {
        const mediaWidth = media.videoWidth || media.naturalWidth || Number(media.getAttribute('width'));
        const mediaHeight = media.videoHeight || media.naturalHeight || Number(media.getAttribute('height'));
        const mediaScale = media.tagName === 'VIDEO' ? parseFloat(item.style.getPropertyValue('--egr-scale')) || 1 : 1;
        if (mediaWidth > 0 && mediaHeight > 0) media.style.setProperty('--egr-real-offset', `${-width * mediaHeight / mediaWidth * Number(item.dataset.realHook || 0) / 100 * mediaScale}px`);
      });
    }
    syncVideo() {
      this.items.forEach((item, index) => {
        const video = item.querySelector('video');
        if (!video) return;
        const play = index === this.active && this.canAnimate && !document.hidden && this.inView !== false && item.dataset.videoFailed !== 'true';
        if (play) {
          if (!video.getAttribute('src')) video.src = video.dataset.src;
          if (video.paused) video.play().catch(() => { item.classList.remove('is-playing'); item.dataset.videoFailed = 'true'; this.updateFlip(); });
        } else { video.pause(); item.classList.remove('is-playing'); }
      });
      this.updateFlip();
    }
    disconnectedCallback() {
      clearTimeout(this.clickTimer);
      cancelAnimationFrame(this.centerFrame);
      this.centerFrame = null;
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  });
}
